import React from "react";
import {connect} from "react-redux";
import memoizeOne from "memoize-one";
import AutoSizer from "react-virtualized-auto-sizer";
import {FixedSizeList, areEqual} from "react-window";
import {
    ROUND_TYPES_DISPLAY_NAMES_MAP,
    ROUND_STOP_TYPES_MAP,
    RoundStopTypeEnum,
    RoundTypeEnum
} from "@nextlot/core/webcast/data/WebcastDataDomain";
import {TaggedLogger} from "@nextlot/core/utilities";
import type {Lot, Round} from "@nextlot/core/webcast/data/data_types_definitions";
import {ReduxWebcastAuctionDataType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastAuctionData";
import {AuctionsManager} from "@nextlot/core/webcast";
import {CurrencyAmountDisplay} from "../CurrencyAmountDisplay";
import {useTranslation} from "react-i18next";
import {ReduxStateType} from "@nextlot/core/webcast/services/DataStore";


type MyRoundListItemDataType = {
    myClosedRounds: Array<Round>,
    lotsMapById: Map<number, Lot>,
    myRoundListItemClick: (round: Round)=>any,
    t?: any
}

type MyRoundListItemDetailsPropsType = {
    round: Round,
    lotsMapById: Map<number, Lot>,
    t:any,
    onClick: ()=>void,
}

const _logger = TaggedLogger.get('BidderMyPurchasesCmp');

// React.memo acts as a PureComponent here (https://reactjs.org/blog/2018/10/23/react-v-16-6.html#reactmemo)
function MyRoundListItemDataCmp(props) {
    const {index, style} = props;
    const itemData: MyRoundListItemDataType = props.data;
    const myClosedRounds = itemData.myClosedRounds;
    const lotsMapById = itemData.lotsMapById;
    const round: Round = myClosedRounds[index];
    const itemsQty = round.lotsIds.map(lotId => lotsMapById.get(lotId).itemsQty);
    const totalPrice = round.highestBid.value * itemsQty[0];
    const t = props.data.t;

    return (
        <div className={`my-round-list-item-container mb-3 ${index === myClosedRounds.length - 1 ? 'last' : ''}`}
             style={style}>
            <div className="my-round-list__item">
                <div className="my-round-list-item__text-container ms-3" onClick={itemData.myRoundListItemClick(round)}>
                    <strong>
                        {ROUND_TYPES_DISPLAY_NAMES_MAP.get(round.roundType)}: {round.lotsIds.map(lotId => `#${lotsMapById.get(lotId).number}`).join(", ")}
                    </strong>
                    <p>
                        {ROUND_STOP_TYPES_MAP.get(round.stopType)} {t('common.for')} <CurrencyAmountDisplay
                        valueCents={round.highestBid.value}/>
                        {round.roundType === RoundTypeEnum.GROUP_CHOICE || round.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN || round.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY
                            ? (<span> (<CurrencyAmountDisplay valueCents={round.highestBid.value}/> {t('common.eachLot')})</span>)
                            : ''
                        }
                        {round.roundType === RoundTypeEnum.SINGLE_BOXLOT && itemsQty[0] > 1 ?
                            (<span> X {itemsQty[0]} = <CurrencyAmountDisplay valueCents={totalPrice}/></span>)
                            :
                            ''
                        }
                    </p>
                </div>
            </div>
        </div>
    )
}

const MyRoundListItem = React.memo(MyRoundListItemDataCmp, areEqual);

const MyRoundListItemDetails = (props: MyRoundListItemDetailsPropsType) => {
    const {round, lotsMapById, t} = props;
    const lots = round.lotsIds.map(lotId => lotsMapById.get(lotId));


    return (
        <div className="bidder-my-purchases-details--item-wrapper d-flex align-items-start flex-column">
            <strong className="pb-2">
                {ROUND_TYPES_DISPLAY_NAMES_MAP.get(round.roundType)}: {lots.map(lot => `#${lot.number}`).join(", ")}
            </strong>
            <div className="bidder-my-purchases-list-item-container">
                {lots.map(lot => {
                    return (
                        <div key={lot.id} className="bidder-my-purchases-list__item">
                            <div className="bidder-my-purchases-list__item-img-wrapper">
                                <img src={lot.defaultImageSmUrl} alt={`Lot #${lot.number}`}/>
                            </div>

                            <div className="bidder-my-purchases-item__text-container ms-3">
                                <strong
                                    className="bidder-my-purchases-item-text__lot-number">
                                    {t('catalogAndMyPurchases.lot')} #{lot.number}
                                </strong>
                                <div className="bidder-my-purchases-item-text__lot-name">{lot.name}</div>
                            </div>
                        </div>

                    )
                })}
            </div>

            <a href="#" className="bidder-my-purchases-item-details__back-btn mt-1" onClick={props.onClick}>
                {t('catalogAndMyPurchases.backToMyPurchases')}
            </a>
        </div>
    )
};




function BidderMyPurchasesCmp(props) {

    _logger.debug('.render()', props);

    const { t, i18n } = useTranslation();

    const [round, setRound] = React.useState<Round>(null);
    const [showMyRoundListItemDetails, setShowMyRoundListItemDetails] = React.useState<boolean>(false);
    const [myUserId, setMyUserId] = React.useState<number>(AuctionsManager.currentWebcastAuctionHandler.auctionConnectData.participantId);


    const handleOnMyRoundListItemClick = (round) => () => {
        setShowMyRoundListItemDetails(true);
        setRound(round);
    };

    const handleOnBackToMyPurchasesClick = () => {
        setShowMyRoundListItemDetails(false);
    };

    const createMyRoundListItemData = memoizeOne((closedRounds: Array<Round>, lotsMapById: Map<number, Lot>, myRoundListItemClick:(round: Round)=>any, t): MyRoundListItemDataType => ({
        myClosedRounds: closedRounds.filter((round: Round) => {
            return (round.highestBid && round.highestBid.userId === myUserId)
                && (round.stopType === RoundStopTypeEnum.SOLD || round.stopType === RoundStopTypeEnum.PENDING)
        }),
        lotsMapById,
        myRoundListItemClick,
        t
    }));

    const webcastAuctionData: ReduxWebcastAuctionDataType = props.webcastAuctionData;
    const closedRounds: Array<Round> = webcastAuctionData.closedRounds;
    const lotsMapById = webcastAuctionData.lotsMapById;

    const itemData = createMyRoundListItemData(closedRounds, lotsMapById, handleOnMyRoundListItemClick, t);

    return (
        <div className="bidder-my-purchases-list-root-container d-flex flex-column flex-grow-1">
            <p className="ps-3 pt-1 mb-0">{t('catalogAndMyPurchases.confirmPurchases')}.</p>

            <div className="bidder-my-purchases-list-wrapper mt-2">
                <AutoSizer>
                    {({height, width}) => (
                        <FixedSizeList
                            className="bidder-my-purchases-list__list-container"
                            height={height}
                            width={width}
                            itemCount={itemData.myClosedRounds.length}
                            itemSize={50}
                            itemData={itemData}
                            itemKey={(index, itemData) => (itemData.myClosedRounds[index].roundId)}
                        >
                            {MyRoundListItem}
                        </FixedSizeList>
                    )}
                </AutoSizer>

                <div className="bidder-my-purchases-details">
                    {showMyRoundListItemDetails &&
                    <MyRoundListItemDetails
                        round={round}
                        lotsMapById={lotsMapById}
                        onClick={handleOnBackToMyPurchasesClick}
                        t={t}
                    />
                    }
                </div>
            </div>
        </div>
    );
}

export default connect(({activeWebcast}: ReduxStateType) => {
    return {
        currentRoundLotsIds: activeWebcast.webcastCurrentRound.lotsIds,
        webcastAuctionData: activeWebcast.webcastAuctionData,
        roundBiddingIsStarted: (activeWebcast.webcastCurrentRoundBidding.askingBidValue > 0),
    };
})(BidderMyPurchasesCmp);




// type MyRoundListItemDataType = {
//     myClosedRounds: Array<Round>,
//     lotsMapById: Map<number, Lot>,
//     myRoundListItemClick: (itemIndex: number)=>{},
// }
//
// type MyRoundListItemPropsType = {
//     data: MyRoundListItemDataType,
//     index: number,
//     style: {}
// };
//
// type MyRoundListItemDetailsPropsType = {
//     round: Round,
//     lotsMapById: Map<number, Lot>,
//
//     onClick: ()=>void,
// }
//
// // React.memo acts as a PureComponent here (https://reactjs.org/blog/2018/10/23/react-v-16-6.html#reactmemo)
// const MyRoundListItem = React.memo(function MyRoundListItemDataCmp(props: MyRoundListItemPropsType) {
//     const {index, style} = props;
//     const itemData: MyRoundListItemDataType = props.data;
//     const myClosedRounds = itemData.myClosedRounds;
//     const lotsMapById = itemData.lotsMapById;
//     const round: Round = myClosedRounds[index];
//     const itemsQty = round.lotsIds.map(lotId => lotsMapById.get(lotId).itemsQty);
//     const totalPrice = round.highestBid.value * itemsQty[0];
//     const t = props.data.t;
//
//     return (
//         <div className={`my-round-list-item-container mb-3 ${index === myClosedRounds.length - 1 ? 'last' : ''}`}
//              style={style}>
//             <div className="my-round-list__item">
//                 <div className="my-round-list-item__text-container ms-3" onClick={itemData.myRoundListItemClick(round)}>
//                     <strong>
//                         {ROUND_TYPES_DISPLAY_NAMES_MAP.get(round.roundType)}: {round.lotsIds.map(lotId => `#${lotsMapById.get(lotId).number}`).join(", ")}
//                     </strong>
//                     <p>
//                         {ROUND_STOP_TYPES_MAP.get(round.stopType)} {t('common.for')} <CurrencyAmountDisplay
//                         valueCents={round.highestBid.value}/>
//                         {round.roundType === RoundTypeEnum.GROUP_CHOICE || round.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN || round.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY
//                             ? (<span> (<CurrencyAmountDisplay valueCents={round.highestBid.value}/> {t('common.eachLot')})</span>)
//                             : ''
//                         }
//                         {round.roundType === RoundTypeEnum.SINGLE_BOXLOT && itemsQty > 1 ?
//                             (<span> X {itemsQty} = <CurrencyAmountDisplay valueCents={totalPrice}/></span>)
//                             :
//                             ''
//                         }
//                     </p>
//                 </div>
//             </div>
//         </div>
//     )
// }, areEqual);
//
// const MyRoundListItemDetails = (props: MyRoundListItemDetailsPropsType) => {
//     const {round, lotsMapById, t} = props;
//     const lots = round.lotsIds.map(lotId => lotsMapById.get(lotId));
//
//
//     return (
//         <div className="bidder-my-purchases-details--item-wrapper d-flex align-items-start flex-column">
//             <strong className="pb-2">
//                 {ROUND_TYPES_DISPLAY_NAMES_MAP.get(round.roundType)}: {lots.map(lot => `#${lot.number}`).join(", ")}
//             </strong>
//             <div className="bidder-my-purchases-list-item-container">
//                 {lots.map(lot => {
//                     return (
//                         <div key={lot.id} className="bidder-my-purchases-list__item">
//                             <div className="bidder-my-purchases-list__item-img-wrapper">
//                                 <img src={lot.defaultImageSmUrl} alt={`Lot #${lot.number}`}/>
//                             </div>
//
//                             <div className="bidder-my-purchases-item__text-container ms-3">
//                                 <strong
//                                     className="bidder-my-purchases-item-text__lot-number">
//                                     {t('catalogAndMyPurchases.lot')} #{lot.number}
//                                 </strong>
//                                 <div className="bidder-my-purchases-item-text__lot-name">{lot.name}</div>
//                             </div>
//                         </div>
//
//                     )
//                 })}
//             </div>
//
//             <a href="#" className="bidder-my-purchases-item-details__back-btn mt-1" onClick={props.onClick}>
//                 {t('catalogAndMyPurchases.backToMyPurchases')}
//             </a>
//         </div>
//     )
// };
//
//
// const _logger = TaggedLogger.get('BidderLotListCmp');
//
//
// type CmpPropsType = {
//     webcastAuctionData: ReduxWebcastAuctionDataType,
//     lotsMapById: Map<number, Lot>,
//     currentRoundLotsIds: ?Array<number>,
//     roundBiddingIsStarted: boolean,
// };
//
// class BidderMyPurchasesCmp extends React.Component<CmpPropsType> {
//
//
//     constructor(props) {
//         super(props);
//
//         this.myUserId = AuctionsManager.currentWebcastAuctionHandler.auctionConnectData.participantId;
//
//         this.state = {
//             round: [],
//             showMyRoundListItemDetails: false,
//         };
//
//     }
//
//     createMyRoundListItemData = memoizeOne((closedRounds, lotsMapById, myRoundListItemClick, t): MyRoundListItemDataType => ({
//         myClosedRounds: closedRounds.filter((round: Round) => {
//             return (round.highestBid && round.highestBid.userId === this.myUserId)
//                 && (round.stopType === RoundStopTypeEnum.SOLD || round.stopType === RoundStopTypeEnum.PENDING);
//         }),
//         lotsMapById,
//         myRoundListItemClick,
//         t
//     }));
//
//     handleOnMyRoundListItemClick = (round) => () => {
//         this.setState({
//             showMyRoundListItemDetails: true,
//             round: round,
//         });
//     };
//
//     handleOnBackToMyPurchasesClick = () => {
//         this.setState({
//             showMyRoundListItemDetails: false
//         })
//     };
//
//
//     render() {
//         _logger.debug('.render()', this.props);
//         const {t} = this.props;
//         const webcastAuctionData: ReduxWebcastAuctionDataType = this.props.webcastAuctionData;
//         const closedRounds: Array<Round> = webcastAuctionData.closedRounds;
//         const lotsMapById = webcastAuctionData.lotsMapById;
//
//         const itemData = this.createMyRoundListItemData(closedRounds, lotsMapById, this.handleOnMyRoundListItemClick, t);
//
//
//         return (
//             <div className="bidder-my-purchases-list-root-container d-flex flex-column flex-grow-1">
//                 <p className="ps-3 pt-1 mb-0">{t('catalogAndMyPurchases.confirmPurchases')}.</p>
//
//                 <div className="bidder-my-purchases-list-wrapper mt-2">
//                     <AutoSizer>
//                         {({height, width}) => (
//                             <FixedSizeList
//                                 className="bidder-my-purchases-list__list-container"
//                                 height={height}
//                                 width={width}
//                                 itemCount={itemData.myClosedRounds.length}
//                                 itemSize={50}
//                                 itemData={itemData}
//                                 itemKey={(index, itemData) => (itemData.myClosedRounds[index].roundId)}
//                             >
//                                 {MyRoundListItem}
//                             </FixedSizeList>
//                         )}
//                     </AutoSizer>
//
//                     <div className="bidder-my-purchases-details">
//                         {this.state.showMyRoundListItemDetails &&
//                         <MyRoundListItemDetails
//                             round={this.state.round}
//                             lotsMapById={lotsMapById}
//                             onClick={this.handleOnBackToMyPurchasesClick.bind(this)}
//                             t={t}
//                         />
//                         }
//                     </div>
//                 </div>
//             </div>
//         );
//     }
// }
//
// export default compose(
//     connect(({activeWebcast}) => {
//         return {
//             currentRoundLotsIds: activeWebcast.webcastCurrentRound.lotsIds,
//             webcastAuctionData: activeWebcast.webcastAuctionData,
//             roundBiddingIsStarted: (activeWebcast.webcastCurrentRoundBidding.askingBidValue > 0),
//         }
//     }),
//     withTranslation()
// )(BidderMyPurchasesCmp)


